import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Trash2, Save, Send, CheckCircle2, Clock, Circle, GripVertical } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const statusIcon = { completed: CheckCircle2, 'in-progress': Clock, pending: Circle };
const statusColor = {
  completed: 'text-green-600 bg-green-50',
  'in-progress': 'text-amber-600 bg-amber-50',
  pending: 'text-gray-400 bg-gray-50',
};

const AdminProjects = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    project_name: '', overall_status: 'Planning', milestones: [], notes: ''
  });

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    const token = getAuthToken();
    try {
      const res = await axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(res.data);
    } catch (e) { console.error(e); }
    setIsLoading(false);
  };

  const loadProject = async (clientId) => {
    const token = getAuthToken();
    setSelectedClient(clientId);
    try {
      const res = await axios.get(`${API}/admin/projects/${clientId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data && res.data.id) {
        setProject(res.data);
        setFormData({
          project_name: res.data.project_name || '',
          overall_status: res.data.overall_status || 'Planning',
          milestones: res.data.milestones || [],
          notes: res.data.notes || ''
        });
      } else {
        setProject(null);
        const client = clients.find(c => c.id === clientId);
        setFormData({
          project_name: client?.event_type ? `${client.name}'s ${client.event_type}` : '',
          overall_status: 'Planning', milestones: [], notes: ''
        });
      }
    } catch (e) {
      setProject(null);
      setFormData({ project_name: '', overall_status: 'Planning', milestones: [], notes: '' });
    }
  };

  const addMilestone = () => {
    setFormData(p => ({
      ...p,
      milestones: [...p.milestones, {
        id: `m-${Date.now()}`, title: '', description: '', status: 'pending',
        target_date: '', completed_date: '', order: p.milestones.length + 1
      }]
    }));
  };

  const updateMilestone = (index, field, value) => {
    setFormData(p => {
      const milestones = [...p.milestones];
      milestones[index] = { ...milestones[index], [field]: value };
      if (field === 'status' && value === 'completed') {
        milestones[index].completed_date = new Date().toISOString().split('T')[0];
      }
      return { ...p, milestones };
    });
  };

  const removeMilestone = (index) => {
    setFormData(p => ({ ...p, milestones: p.milestones.filter((_, i) => i !== index) }));
  };

  const handleSave = async (notify = false) => {
    if (!formData.project_name) { toast.error('Project name is required'); return; }
    setIsSaving(true);
    const token = getAuthToken();
    try {
      if (project) {
        await axios.put(`${API}/admin/projects/${selectedClient}`, { ...formData, notify_client: notify }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/admin/projects/${selectedClient}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success(notify ? 'Saved & notification sent!' : 'Project guide saved!');
      loadProject(selectedClient);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setIsSaving(false);
  };

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  const completed = formData.milestones.filter(m => m.status === 'completed').length;
  const progress = formData.milestones.length ? Math.round((completed / formData.milestones.length) * 100) : 0;

  return (
    <div data-testid="admin-projects">
      <h1 className="font-heading text-3xl text-[#2C2C2C] mb-8">Project Guides</h1>

      {/* Client selector */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        {clients.map(c => (
          <button
            key={c.id}
            data-testid={`select-client-${c.id}`}
            onClick={() => loadProject(c.id)}
            className={`p-4 rounded-2xl text-left transition-all duration-200 ${
              selectedClient === c.id
                ? 'bg-[#4A5D23] text-white shadow-lg'
                : 'bg-white shadow-soft hover:shadow-md'
            }`}
          >
            <div className="font-heading text-lg truncate">{c.name}</div>
            <div className={`text-sm ${selectedClient === c.id ? 'text-white/70' : 'text-[#6B7280]'}`}>
              {c.event_type || 'No event type'}
            </div>
          </button>
        ))}
      </div>

      {!selectedClient && (
        <div className="bg-white rounded-2xl shadow-soft p-12 text-center">
          <p className="text-[#6B7280]">Select a client above to manage their project guide.</p>
        </div>
      )}

      {selectedClient && (
        <div className="bg-white rounded-2xl shadow-soft p-8 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-2xl text-[#2C2C2C]">
              {project ? 'Edit Project Guide' : 'Create Project Guide'}
            </h2>
            {formData.milestones.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-[#4A5D23] rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
                <span className="text-sm font-medium text-[#4A5D23]">{progress}%</span>
              </div>
            )}
          </div>

          {/* Project Info */}
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-sm font-medium mb-2 block">Project Name *</Label>
              <Input data-testid="project-name" value={formData.project_name} onChange={(e) => setFormData(p => ({ ...p, project_name: e.target.value }))} className="input-elegant" placeholder="e.g., Smith Wedding" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Overall Status</Label>
              <Select value={formData.overall_status} onValueChange={(v) => setFormData(p => ({ ...p, overall_status: v }))}>
                <SelectTrigger className="input-elegant"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Planning">Planning</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Final Review">Final Review</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Milestones */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <Label className="text-sm font-medium">Milestones</Label>
              <Button data-testid="add-milestone-btn" onClick={addMilestone} variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37]">
                <Plus size={14} className="mr-1" /> Add Milestone
              </Button>
            </div>

            {formData.milestones.length === 0 && (
              <p className="text-sm text-[#6B7280] text-center py-8 bg-[#F5F5F0] rounded-xl">No milestones yet. Add one to start tracking progress.</p>
            )}

            <div className="space-y-3">
              {formData.milestones.map((m, i) => {
                const Icon = statusIcon[m.status] || Circle;
                return (
                  <div key={m.id} data-testid={`milestone-${i}`} className={`flex gap-3 p-4 rounded-xl border border-[#E8DFD0] ${m.status === 'completed' ? 'bg-green-50/50' : 'bg-white'}`}>
                    <div className="flex-shrink-0 pt-1">
                      <GripVertical size={16} className="text-[#E8DFD0]" />
                    </div>
                    <div className="flex-1 grid md:grid-cols-12 gap-3">
                      <div className="md:col-span-3">
                        <Input value={m.title} onChange={(e) => updateMilestone(i, 'title', e.target.value)} className="input-elegant text-sm" placeholder="Milestone title" />
                      </div>
                      <div className="md:col-span-3">
                        <Input value={m.description} onChange={(e) => updateMilestone(i, 'description', e.target.value)} className="input-elegant text-sm" placeholder="Description" />
                      </div>
                      <div className="md:col-span-2">
                        <Input type="date" value={m.target_date} onChange={(e) => updateMilestone(i, 'target_date', e.target.value)} className="input-elegant text-sm" />
                      </div>
                      <div className="md:col-span-2">
                        <Select value={m.status} onValueChange={(v) => updateMilestone(i, 'status', v)}>
                          <SelectTrigger className={`text-sm rounded-full ${statusColor[m.status]}`}><Icon size={14} className="mr-1" /><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in-progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2 flex justify-end">
                        <Button variant="ghost" size="sm" onClick={() => removeMilestone(i)} className="text-red-400 hover:text-red-600">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} className="input-elegant min-h-[80px]" placeholder="Internal notes about this project..." />
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t border-[#E8DFD0]">
            <Button data-testid="save-project-btn" onClick={() => handleSave(false)} disabled={isSaving} className="bg-[#4A5D23] hover:bg-[#6B7F3A] text-white">
              <Save size={16} className="mr-2" /> {isSaving ? 'Saving...' : 'Save Project'}
            </Button>
            <Button data-testid="save-notify-btn" onClick={() => handleSave(true)} disabled={isSaving} variant="outline" className="border-[#D4AF37] text-[#D4AF37]">
              <Send size={16} className="mr-2" /> Save & Notify Client
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;
